---
title: Hotkeys
lede: Every key Airlock binds, by where it works.
order: 9
part: Find your way
---

`⌘⇧P` opens the command palette, and so does **Search** in the window bar. It lists sessions, projects, workspaces and commands in one list, with each command's key beside it.

Keys are written the macOS way. On Windows and Linux, `⌘` is **Ctrl** and `⌥` is **Alt**; `⌃` is Ctrl everywhere. A few keys exist on macOS only — see [the last section](#on-windows-and-linux).

![The command palette](media/palette.webp)

### Getting around

| Key | What it does |
|---|---|
| `⌘⇧P` | Command palette |
| `⌃1` … `⌃9` | Select the nth session of this workspace |
| `⌃⇧1` … `⌃⇧9` | Switch to the nth workspace |
| `⌘⌥←` / `⌘⌥→` | Previous / next session |
| `⌃⇥` / `⌃⇧⇥` | Next / previous session waiting for you, in any workspace |
| `⌘B` | Collapse or expand the navigator |
| `⌘J` | Open or close the Agent panel |
| `⌥⌘W` | Open or close a session's run panel |
| `⌥W` | Pick or change a session's workflow, unless a terminal or a text field has the keyboard |
| `⌥⌘J` | Show or hide the dock |
| `⌘⇧O` | Open the surface in a window of its own |
| `⌘,` | Settings |

### Inside a session

| Key | What it does |
|---|---|
| `⌘1` … `⌘5` | Agent, Files, Changes, Browser, API console |
| `⌘⌥S` | Sync from the source branch |
| `⌘⇧↩` | Merge the ticked files |
| `⌘⌥R` | Start all projects |
| `⌘F` | Find in logs |
| `⌘⇧E` | Export the session |
| `⌘⇧⌫` | Close the session |

### Making things

| Key | What it does |
|---|---|
| `⌘N` | New task |
| `⌘⇧N` | New task from a pull request |
| `⌘⌥N` | Investigate the clipboard |
| `⌘⌥2` | Workflows |
| `⌘⌥3` | Vault |
| `⌘⇧,` | Workspace settings |
| `⌘⌥D` | Diagnostics |

### In forms and notes

| Key | What it does |
|---|---|
| `⌘↩` | Press the task form's main button from its prompt; save or send a note, or an answer on a pull request; run a query in Diagnostics |
| `↩` | Send a question in the Agent panel (`⇧↩` adds a line); go from the task form's title to its prompt |
| `Esc` | Close the task form, a note, a menu, the palette or the Agent panel |
| `⌘S` | Save a settings page or a file in the editor |
| `⌘B` `⌘I` `⌘U` `⌘E` `⌘K` | Bold, italic, underline, code and link in a Markdown file |
| `⌘⇧X` | Strikethrough in a Markdown file |

### In the Files surface

These are the **Standard** keys. **Settings** can switch to the VS Code, IntelliJ or Vim keys; see [Files](/files).

| Key | What it does |
|---|---|
| `⇧` twice, or `⌘P` | Go to File |
| `⌘T` | Go to Symbol |
| `⌘E` | Recent Files |
| `⌘⇧F` / `⌘⇧H` | Find in Files / Replace in Files |
| `⌘[` / `⌘]`, or `⌃-` / `⌃⇧-` | Back / Forward |
| `F12`, `⌘F12`, `⇧F12` | Go to definition, go to implementation, find references |
| `F2` | Rename everywhere |
| `⌘.` | Quick fixes |
| `F8` / `⇧F8` | Next / previous error |
| `⌘⇧Space` | Parameter info |
| `⌘⇧O` | File structure |
| `⇧⌥F` or `⌘⌥L` | Format the file |
| `⌃⌥⇧↓` / `⌃⌥⇧↑` | Next / previous change |
| `⌘⇧U` | Flip the selection's case |

In a spreadsheet, `⌘A` selects every cell, `⌘C` copies, `⌥↓` opens the column's filter, and `⇧` with an arrow extends the selection.

### In terminals

| Key | What it does |
|---|---|
| `⌘C` | Copy the selection (`Ctrl+Shift+C` on Windows and Linux) |
| `⌃C` | Interrupt, as in any terminal |
| `⌘⇧D` | In a VM shell: list the files on screen, to download one |
| `⇧↩` | In an agent's terminal: a new line in its prompt |

### In lists

A list row takes the focus with Tab. Arrow keys, `Home` and `End` move between rows; these act on the focused row.

| Key | What it does |
|---|---|
| `↩` | Open it |
| `⇧F10`, or the Menu key | Its actions menu |
| `␣` | Tick or untick a file in **Changes**; open a board card |
| `⇧←` / `⇧→` | Move a board card to the lane before or after |
| `⌥↑` / `⌥↓` | Move a tab up or down the surfaces column; move a task in the Board's list |
| `⌥⇧↑` / `⌥⇧↓` | Move a session in the navigator, a project in the browser's list, or a row of the console's collection |
| `⌥⇧←` / `⌥⇧→` | Move a dock tab |
| `⌫` | End a side agent, from its row in the surfaces column; close an open file's tab; delete a row of the console's collection |
| `F2` | Rename a row of the console's collection, or a flow |
| `⇧↩` | Add a project to the logs shown, in the dock's project list |
| `⌥↩` | In the pull request picker: review it instead of working on it, or the reverse |
| `1` … `9` | Pick a project while the browser's project list is open |

In **Changes**, `↑` and `↓` scroll the diff and carry on into the next file.

To move a statement to another RFC in **Rules**, press `␣` on it, choose with `↑` and `↓`, then `␣` to drop it or `Esc` to cancel. On the graph in Diagnostics, the arrows pan, `+` and `−` zoom and `0` fits it.

### On the Change Map

| Key | What it does |
|---|---|
| `1` `2` | System, Components |
| `F` | Fit the change on screen |
| `+` / `−` | Zoom in / out |
| `/` | Find a part |
| `[` / `]` | Previous / next step of a flow |
| `D` | Read the selected part's code |
| `Esc` | Clear the selection |

### From the menu bar

The menus' own keys. On macOS: `⌘+`, `⌘−` and `⌘0` zoom; `⌃⌘F` is full screen; `⌘R` and `⌘⇧R` reload the window; `⌘W` closes it; `⌘M` minimizes; `⌘H` hides Airlock and `⌘Q` quits it, while sessions keep running. The Edit menu holds the usual undo, redo, cut, copy, paste and select all.

### On Windows and Linux

These keys are macOS only, because Ctrl with the same letter belongs to the terminals in the dock (tmux, readline):

| On macOS | On Windows and Linux |
|---|---|
| `⌘B`, `⌘J`, `⌥⌘W`, `⌥⌘J` | Use the **View** menu |
| `⌘F` | **Edit › Find in logs** |
| `⌘1` … `⌘5` | Click the surface, or use the **View** menu |

Your desktop can also take a key first. The palette lists every command either way.
